// Quote progress on the Plans page (flow#735, oracle#131). The quotes GET carries
// a progress id; Oracle reports how far the quote has got under it, and the page
// polls GET /api/consults/quotes/progress/{id}/ meanwhile. A snapshot is Oracle's
// stage codes and counts only; the words are ours.
import { v4 as uuidv4 } from 'uuid'

// 32 hex characters: within Oracle's [A-Za-z0-9_-]{16,64}
export function newProgressId () {
    return uuidv4().replace(/-/g, '')
}

// While the plans are gathered, the label follows the first track still running.
// Each ends in "..." like the spinner's own messages, whose line it takes over.
const TRACK_ORDER = ['mapdp', 'supp', 'lapro']

function trackLabel (track, { stage, done, total }) {
    if (track === 'mapdp') {
        if (stage === 'plans') {
            return total ? `Checking plans in your area (${done} of ${total})...` : 'Checking plans in your area...'
        }
        if (stage === 'drugs' || stage === 'drug_costs') { return 'Pricing your prescriptions...' }
        if (stage === 'networks') { return "Checking your doctors' networks..." }
        return 'Finding plans in your area...'
    }
    if (track === 'supp') { return 'Getting Medigap rates...' }
    return 'Checking with the carriers...'
}

const STAGE_LABELS = {
    county: 'Looking up your area...',
    post: 'Putting your quotes together...',
    ranking: 'Ranking your options...',
    extras: 'Adding plan details...',
    done: 'Almost done...',
}

export function progressLabel (snapshot) {
    const { stage, tracks, plans } = snapshot || {}
    if (stage === 'gathering') {
        // Oracle's count of every plan the quote checks, Medigap included (oracle
        // 5f64ac8). Its total grows once the Medigap count is known. An older
        // Oracle doesn't send it, and 0 of 0 means nothing is counted yet: both
        // fall through to the tracks' own labels.
        if (plans?.total > 0 && plans.done < plans.total) {
            return `Checking plans in your area (${plans.done} of ${plans.total})...`
        }
        const track = TRACK_ORDER.find(t => tracks?.[t] && tracks[t].stage !== 'done')
        return track ? trackLabel(track, tracks[track]) : STAGE_LABELS.post
    }
    return STAGE_LABELS[stage] || 'Getting your quotes...'
}

export function progressPercent (snapshot) {
    const fraction = snapshot?.fraction
    if (typeof fraction !== 'number') { return 0 }
    return Math.min(100, Math.max(0, Math.round(fraction * 100)))
}

// Polls `fetch` every `interval` ms, waiting out each poll before the next, and
// hands `onProgress` every snapshot that isn't behind one it already had (two
// polls can come back out of order). A failed poll is skipped: the first ones
// 404 until Oracle has claimed the id. Returns stop().
export function pollProgress ({ fetch, onProgress, interval = 1000 }) {
    let stopped = false
    let timer = null
    let best = -1

    const tick = async () => {
        let snapshot = null
        try {
            snapshot = await fetch()
        } catch (e) {
            snapshot = null
        }
        if (stopped) { return }
        if (typeof snapshot?.fraction === 'number' && snapshot.fraction >= best) {
            best = snapshot.fraction
            onProgress(snapshot)
        }
        timer = setTimeout(tick, interval)
    }
    timer = setTimeout(tick, interval)

    return () => {
        stopped = true
        clearTimeout(timer)
    }
}
